import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { Callout, CopyableValue, SectionCard, useToast } from '@/components/ui';
import {
    checkAppConnection,
    normalizeApiErrorDetails,
    updateAdminApp,
    type ConnectionCheckProbe,
} from '@/api/console';
import { OrgAdminApiError } from '@/api/orgAdmin';
import { AppUriListField } from '../apps/AppUriListField';
import type { AppDetailPageVM } from './useAppDetailPage';

/**
 * EAA-1817 (frame 50): Kết nối kỹ thuật tab.
 *
 * Omitted vs the mock, with no fabricated substitute: "Cách cấp phiên" (grant
 * types + session TTL — no field on `Application` backs any of it).
 *
 * EAA-1846: "Kiểm tra kết nối" now calls `POST /admin/apps/{code}/connection-check`
 * (shipped under EAA-1810), which diagnoses whether the realm still has the
 * client, whether the redirect URIs match this registry row, whether anything
 * emits the audience, whether the app's `/roles` answers, and whether a
 * back-channel logout URL is registered and listening. Gated on `vm.canManage`
 * — the server itself gates on `APPS_CREDENTIALS`, a permission this FE has no
 * dedicated capability key for yet (`console.apps.manage` is the closest one
 * already wired into `useAppDetailPage`), so this is best-effort UI gating, not
 * the enforcement boundary; the server still 403s a caller without it.
 *
 * Redirect-URI environment tags ("Production"/"Thử
 * nghiệm"/...) are also omitted — `redirect_uris` is a flat `string[]`, no
 * environment field exists. What ships: Client ID / rotate-secret / issuer
 * (real fields, `createAppClient`'s one-time-reveal contract respected — no
 * secret VALUE is ever displayed, only the rotate action), the editable
 * redirect-URI lists (`PATCH /admin/apps/{code}` genuinely accepts these),
 * the "Dán cho đội kỹ thuật" snippet (fully derivable from fields already on
 * hand), and the connection-check probe list.
 */

const PROBE_LABEL_KEYS: Record<string, string> = {
    kc_client_exists: 'console.appDetail.connection.check.probeClient',
    redirect_uris: 'console.appDetail.connection.check.probeRedirectUris',
    audience_mapper: 'console.appDetail.connection.check.probeAudience',
    app_roles_catalog: 'console.appDetail.connection.check.probeRolesCatalog',
    backchannel_logout: 'console.appDetail.connection.check.probeBackchannelLogout',
    app_me_access: 'console.appDetail.connection.check.probeMeAccess',
};

function ProbeRow({ probe }: { probe: ConnectionCheckProbe }) {
    const { t } = useTranslation();
    const label = PROBE_LABEL_KEYS[probe.id]
        ? t(PROBE_LABEL_KEYS[probe.id])
        : probe.id;
    return (
        <li className="app-detail-probe-row">
            <span className={`app-detail-probe-status is-${probe.status}`}>
                {t(`console.appDetail.connection.check.status.${probe.status}`)}
            </span>
            <div className="app-detail-probe-body">
                <strong>{label}</strong>
                {/* Server-composed, per-app diagnostic text (URIs, counts) —
                    not pre-translatable, same precedent as
                    AppOnboardingModal's `revealed.warning`. */}
                <span>{probe.detail}</span>
                {probe.remedy && <span className="app-detail-probe-remedy">{probe.remedy}</span>}
            </div>
        </li>
    );
}

function rateLimitRetrySeconds(error: unknown): string | null {
    if (!(error instanceof OrgAdminApiError)) return null;
    return normalizeApiErrorDetails(error.details).retry_after_seconds?.[0] ?? null;
}

export function AppDetailConnectionTab({ vm }: { vm: AppDetailPageVM }) {
    const { t } = useTranslation();
    const config = useRuntimeConfig();
    const { accessToken } = useAuth();
    const queryClient = useQueryClient();
    const toast = useToast();
    const app = vm.app;

    const [redirectUris, setRedirectUris] = useState<string[]>(app?.redirect_uris ?? ['']);
    const [postLogoutUris, setPostLogoutUris] = useState<string[]>(
        app?.post_logout_redirect_uris ?? ['']
    );

    const saveMutation = useMutation({
        mutationFn: (body: { redirect_uris?: string[]; post_logout_redirect_uris?: string[] }) =>
            updateAdminApp(config.apiBase, accessToken as string, vm.code, body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin-apps'] });
            toast.success(t('console.appDetail.connection.saveSuccess'));
        },
        onError: () => toast.error(t('console.appDetail.connection.saveError')),
    });

    const checkMutation = useMutation({
        mutationFn: () => checkAppConnection(config.apiBase, accessToken as string, vm.code),
        onError: (error) => {
            const retryAfter = rateLimitRetrySeconds(error);
            toast.error(
                retryAfter
                    ? t('console.appDetail.connection.check.cooldownError', { seconds: retryAfter })
                    : t('console.appDetail.connection.check.error')
            );
        },
    });

    if (!app) return null;

    const snippet = [
        `issuer:    ${config.authority}`,
        `client_id: ${app.kc_client_id}`,
        `audience:  ${app.audience}`,
    ].join('\n');

    function copy(value: string) {
        void navigator.clipboard.writeText(value).then(
            () => toast.success(t('console.appDetail.connection.copied')),
            () => toast.error(t('console.appDetail.connection.copyFailed'))
        );
    }

    return (
        <div className="app-detail-grid">
            <div className="app-detail-grid__main">
                <SectionCard title={t('console.appDetail.connection.infoCardTitle')}>
                    <div className="app-detail-code-field">
                        <span className="app-detail-code-field__label">
                            {t('console.appDetail.connection.clientId')}
                        </span>
                        {app.kc_client_id ? (
                            <CopyableValue
                                value={app.kc_client_id}
                                ariaLabel={t('console.appDetail.connection.clientId')}
                            />
                        ) : (
                            <code>—</code>
                        )}
                    </div>
                    <div className="app-detail-code-field">
                        <span className="app-detail-code-field__label">
                            {t('console.appDetail.connection.clientSecret')}
                        </span>
                        <span className="app-detail-code-field__box">
                            <code>{'•'.repeat(24)}</code>
                            <span className="app-detail-code-field__meta">
                                {app.kc_secret_rotated_at
                                    ? t('console.appDetail.connection.secretRotatedAt', {
                                          date: new Date(app.kc_secret_rotated_at).toLocaleDateString(
                                              'vi-VN'
                                          ),
                                      })
                                    : t('console.appDetail.connection.secretNeverRotated')}
                            </span>
                        </span>
                    </div>
                    {/* EAA-1817 (frame 50): the "Issuer" copy row the mock draws alongside
                        Client ID/Chuỗi bí mật — missing from the shipped page entirely
                        until now, `config.authority` was already read for the snippet
                        below but never surfaced as its own row. */}
                    <div className="app-detail-code-field">
                        <span className="app-detail-code-field__label">
                            {t('console.appDetail.connection.issuer')}
                        </span>
                        <CopyableValue
                            value={config.authority}
                            ariaLabel={t('console.appDetail.connection.issuer')}
                        />
                    </div>
                    <Callout tone="warning" label={t('console.appDetail.connection.rotateWarningLabel')}>
                        {t('console.appDetail.connection.rotateWarningBody')}
                    </Callout>
                </SectionCard>

                <SectionCard title={t('console.appDetail.connection.redirectCardTitle')}>
                    <p className="app-detail-hint">{t('console.appDetail.connection.redirectIntro')}</p>
                    <AppUriListField
                        legend={t('console.appDetail.connection.redirectLegend')}
                        values={redirectUris}
                        onChange={setRedirectUris}
                        onBlur={() => saveMutation.mutate({ redirect_uris: redirectUris.filter(Boolean) })}
                        placeholder="https://…/auth/callback"
                        addLabel={t('console.appDetail.connection.addRedirect')}
                    />
                    <AppUriListField
                        legend={t('console.appDetail.connection.postLogoutLegend')}
                        values={postLogoutUris}
                        onChange={setPostLogoutUris}
                        onBlur={() =>
                            saveMutation.mutate({
                                post_logout_redirect_uris: postLogoutUris.filter(Boolean),
                            })
                        }
                        placeholder="https://…/logged-out"
                        addLabel={t('console.appDetail.connection.addPostLogout')}
                    />
                </SectionCard>
            </div>

            <div className="app-detail-grid__side">
                <SectionCard title={t('console.appDetail.connection.pasteCardTitle')}>
                    <p className="app-detail-hint">{t('console.appDetail.connection.pasteIntro')}</p>
                    <pre className="app-detail-code-block">{snippet}</pre>
                    <div className="app-detail-button-row">
                        <button type="button" className="portal-btn" onClick={() => copy(snippet)}>
                            {t('console.appDetail.connection.copyAction')}
                        </button>
                    </div>
                </SectionCard>

                {vm.canManage && (
                    <SectionCard title={t('console.appDetail.connection.check.cardTitle')}>
                        <p className="app-detail-hint">
                            {t('console.appDetail.connection.check.intro')}
                        </p>
                        <div className="app-detail-button-row">
                            <button
                                type="button"
                                className="portal-btn"
                                onClick={() => checkMutation.mutate()}
                                disabled={checkMutation.isPending}
                            >
                                {checkMutation.isPending
                                    ? t('console.appDetail.connection.check.pending')
                                    : t('console.appDetail.connection.check.button')}
                            </button>
                        </div>
                        {checkMutation.data && (
                            <>
                                <Callout
                                    tone={
                                        checkMutation.data.overall === 'ok'
                                            ? 'info'
                                            : checkMutation.data.overall === 'degraded'
                                              ? 'warning'
                                              : 'danger'
                                    }
                                >
                                    {t(
                                        `console.appDetail.connection.check.overall.${checkMutation.data.overall}`,
                                        {
                                            time: new Date(
                                                checkMutation.data.checked_at
                                            ).toLocaleTimeString('vi-VN'),
                                        }
                                    )}
                                </Callout>
                                <ul className="app-detail-probe-list">
                                    {checkMutation.data.probes.map((probe) => (
                                        <ProbeRow key={probe.id} probe={probe} />
                                    ))}
                                </ul>
                            </>
                        )}
                    </SectionCard>
                )}
            </div>
        </div>
    );
}

export default AppDetailConnectionTab;
